import {
  createContext,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from 'react'

import type { ProductVariant, Product, VariantSlug } from 'data/product'
import { BOTTLE_PATH, MEET_PATH, isPathActive, urlWithVariant } from 'lib/urls'
import { navigateWithoutScroll } from 'lib/navigateWithoutScroll'

const sessionStorageKey = 'selectedVariantSlug'

interface ProductVariantContextState {
  currentVariant: ProductVariant
  setVariant: (variant: ProductVariant) => void
  product: Product
}

export const ProductVariantContext = createContext<ProductVariantContextState>({
  currentVariant: undefined as any,
  setVariant: () => {},
  product: undefined as any,
})

interface Props {
  product: Product
  initialVariant?: VariantSlug
  children: ReactNode
}

export const defaultVariantName: VariantSlug = 'googieSilverline'

const resolveVariant = (variants: ProductVariant[], initialVariant: string) => {
  const defaultVariant =
    variants.find((v) => v.slug === initialVariant) || variants[0]

  if (typeof window === 'undefined') {
    return defaultVariant
  }

  const storedVariant = window.sessionStorage.getItem(sessionStorageKey)
  if (storedVariant) {
    const variant = variants.find((v) => v.slug === storedVariant)
    if (variant) {
      return variant
    }
  }

  return defaultVariant
}

const ProductVariantProvider: FC<Props> = ({
  children,
  product,
  initialVariant,
}) => {
  const [currentVariant, setVariant] = useState(() =>
    resolveVariant(product.variants, initialVariant ?? defaultVariantName)
  )

  useEffect(() => {
    let newPath: string | undefined

    if (isPathActive(BOTTLE_PATH, window.location.pathname)) {
      newPath = urlWithVariant(BOTTLE_PATH, currentVariant)
    } else if (isPathActive(MEET_PATH, window.location.pathname)) {
      newPath = urlWithVariant(MEET_PATH, currentVariant)
    }

    if (newPath && window.location.pathname !== newPath) {
      navigateWithoutScroll(newPath)
    }

    // Store variant in session storage for persistence across navigation
    window.sessionStorage.setItem(sessionStorageKey, currentVariant.slug)
  }, [currentVariant])

  return (
    <ProductVariantContext.Provider
      value={{
        currentVariant,
        setVariant,
        product,
      }}
    >
      {children}
    </ProductVariantContext.Provider>
  )
}

export default ProductVariantProvider
